import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Component, Inject, OnInit, PLATFORM_ID, Renderer2, DOCUMENT } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { LayoutService } from './core/layout.service';
import { GtmService } from './core/gtm.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit {
  private readonly locales = ['ar', 'de', 'es', 'fr', 'id', 'it', 'ja', 'pl', 'pt', 'ru', 'zh'];
  public isHandset$: Observable<boolean>;

  constructor(
    private renderer: Renderer2,
    private layoutService: LayoutService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    private gtmService: GtmService,
    private router: Router
  ) {
    this.gtmService.addGtm();
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const html = this.document.querySelector('html');

      this.renderer.addClass(html, 'js');

      this.isHandset$ = this.layoutService.isHandset$.pipe(
        tap(isHandset =>
          isHandset ? this.renderer.removeClass(html, 'no-mobile') : this.renderer.addClass(html, 'no-mobile')
        )
      );
    }

    this.setCanonicalTag(this.router.url);

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe((e: NavigationEnd) => {
      this.setCanonicalTag(e.urlAfterRedirects);
    });
  }

  private setCanonicalTag(url: string): void {
    const cleanUrl = this.normalizeCanonicalPath(url);
    const canonicalUrl = 'https://www.caseactive.com' + cleanUrl;

    let link = this.document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'rel', 'canonical');
      this.renderer.appendChild(this.document.head, link);
    }
    this.renderer.setAttribute(link, 'href', canonicalUrl);

    this.setHreflangTags(cleanUrl, this.locales);
  }

  private setHreflangTags(currentPath: string, locales: string[]): void {
    this.document.querySelectorAll('link[rel="alternate"]').forEach(el => el.remove());

    const base = 'https://www.caseactive.com';
    const segments = currentPath.split('/').filter(Boolean);
    const hasLocale = locales.includes(segments[0]);

    const englishPath = hasLocale ? '/' + segments.slice(1).join('/') : currentPath;

    const hreflangEntries = [
      { hreflang: 'en', href: `${base}${englishPath || '/'}` },
      { hreflang: 'x-default', href: `${base}${englishPath || '/'}` },
      ...locales.map(locale => ({
        hreflang: locale,
        href: `${base}/${locale}${englishPath === '/' ? '' : englishPath}`
      }))
    ];

    hreflangEntries.forEach(({ hreflang, href }) => {
      const link = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'rel', 'alternate');
      this.renderer.setAttribute(link, 'hreflang', hreflang);
      this.renderer.setAttribute(link, 'href', href);
      this.renderer.appendChild(this.document.head, link);
    });
  }

  private normalizeCanonicalPath(url: string): string {
    const path = (url.split('?')[0].replace(/\/+$/, '') || '/');
    const segments = path.split('/').filter(Boolean);
    const hasLocale = this.locales.includes(segments[0]);
    const offset = hasLocale ? 1 : 0;

    if (segments[offset] === 'docs') {
      segments[offset] = 'documentation';
    }

    if (segments[offset] === 'term-of-service') {
      segments[offset] = 'terms-of-service';
    }

    if (segments[offset] === 'home') {
      return hasLocale ? `/${segments[0]}` : '/';
    }

    if (segments[offset] === 'solutions') {
      segments.splice(offset, segments.length - offset, 'overview');
    }

    return segments.length ? `/${segments.join('/')}` : '/';
  }
}
